/**
 * ReconcileSheet — „Transaktion zuordnen“ wie sevdesk:
 *   Beleg zuordnen   Vorschläge + Suche, mehrere Rechnungen/Belege, Beträge editierbar,
 *                    Differenz MUSS begründet werden (Teilzahlung, Skonto, Kosten Zahlungsverkehr)
 *   Ohne Beleg       Kredit, Privatentnahme, Geldtransit, Gebühren, anderes Konto
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Check, Loader2, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { fmtEUR } from '@/lib/buchhaltungApi';
import {
  txRest, isIncoming, computeDifference, differenceReasons, rankCandidates, searchOpenItems, ACCOUNT_CATEGORIES,
} from '@/lib/reconcileLogic';
import { reconcile } from '@/lib/reconcileApi';
import { BTN_PRIMARY, BTN_SECONDARY, FIELD, LABEL } from '@/components/ausgaben/dna';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('de-DE') : '—');
const parseAmount = (v) => {
  const s = String(v ?? '').trim();
  const n = Number(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : NaN;
};
const toInput = (n) => (Math.round(Number(n) * 100) / 100).toFixed(2).replace('.', ',');

function Segment({ value, onChange, options }) {
  return (
    <div className="grid grid-cols-2 gap-1 rounded-lg bg-dna-warm p-1" role="tablist">
      {options.map((o) => (
        <button key={o.key} type="button" role="tab" aria-selected={value === o.key} onClick={() => onChange(o.key)}
          className={cn('h-10 rounded-md text-[14px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dna-focus',
            value === o.key ? 'bg-white text-dna-primary shadow-sm' : 'text-dna-muted hover:text-dna-primary')}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ItemRow({ item, selected, reasons, onToggle }) {
  return (
    <button type="button" onClick={onToggle} aria-pressed={selected}
      className={cn('flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dna-focus',
        selected ? 'border-dna-primary bg-dna-warm' : 'border-dna-line bg-white hover:bg-dna-warm')}>
      <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded border', selected ? 'border-dna-primary bg-dna-primary text-white' : 'border-dna-field')}>
        {selected && <Check className="h-3.5 w-3.5" aria-hidden="true" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[14px] font-semibold text-dna-text">
          <span className="font-mono">{item.invoice_no || '—'}</span> · {item.partner_name || '—'}
        </span>
        <span className="block text-[12px] text-dna-muted">
          {fmtDate(item.invoice_date)}{item.status === 'partial' ? ' · teilbezahlt' : ''}
          {reasons?.length ? ` · passt: ${reasons.join(', ')}` : ''}
        </span>
      </span>
      <span className="shrink-0 text-right text-[14px] font-semibold tabular-nums text-dna-text">{fmtEUR(item.open_amount)}</span>
    </button>
  );
}

export default function ReconcileSheet({ tx, openItems, open, onOpenChange, initialMode = 'items', initialCategory = null, preselect = null, actor, onDone }) {
  const [mode, setMode] = useState(initialMode);
  const [query, setQuery] = useState('');
  const [sel, setSel] = useState({});        // open_item_id → Betrag (Text)
  const [reason, setReason] = useState(null);
  const [category, setCategory] = useState(initialCategory || 'transit');
  const [interest, setInterest] = useState('');
  const [accountNo, setAccountNo] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !tx) return;
    setMode(initialMode);
    setCategory(initialCategory || 'transit');
    setQuery(''); setReason(null); setInterest(''); setAccountNo(''); setNote('');
    setSel(preselect ? { [preselect.id]: toInput(preselect.open_amount) } : {});
  }, [open, tx?.id, initialMode, initialCategory, preselect?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const incoming = isIncoming(tx);
  const rest = tx ? txRest(tx) : 0;
  const byId = useMemo(() => new Map((openItems || []).map((i) => [i.id, i])), [openItems]);
  const suggestions = useMemo(() => (tx ? rankCandidates(tx, openItems, { limit: 5 }) : []), [tx, openItems]);
  const results = useMemo(() => (query.trim() ? searchOpenItems(openItems, query, incoming).slice(0, 30) : []), [openItems, query, incoming]);

  const selections = Object.entries(sel).map(([id, v]) => ({ id: Number(id), amount: parseAmount(v) }));
  const invalid = selections.some((s) => !(s.amount > 0) || s.amount > Number(byId.get(s.id)?.open_amount || 0) + 0.005);
  const diff = computeDifference(rest, selections);
  const reasons = differenceReasons(diff.kind, incoming);
  const reasonOk = diff.kind === 'exakt' || reasons.some((r) => r.key === reason);

  useEffect(() => { if (!reasons.some((r) => r.key === reason)) setReason(null); }, [diff.kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (item) => setSel((p) => {
    const next = { ...p };
    if (next[item.id] !== undefined) delete next[item.id];
    else next[item.id] = toInput(item.open_amount);
    return next;
  });

  const submit = async () => {
    setBusy(true);
    try {
      const payload = mode === 'items'
        ? { mode: 'items', items: selections.map((s) => ({ open_item_id: s.id, amount: s.amount })), ...(diff.kind !== 'exakt' ? { difference: { reason } } : {}), note: note || undefined }
        : { mode: 'account', category, note: note || undefined,
            ...(category === 'kredit' && interest ? { interest: parseAmount(interest) } : {}),
            ...(category === 'konto' ? { account_no: accountNo.trim() } : {}) };
      const res = await reconcile(tx.id, payload, actor);
      toast.success(res?.status === 'partial'
        ? `Teilweise zugeordnet — ${fmtEUR(res.rest)} des Umsatzes noch offen.`
        : 'Zugeordnet und gebucht.');
      onDone?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const accountValid = mode === 'account' && (category !== 'konto' || /^\d{4,8}$/.test(accountNo.trim()))
    && (!(category === 'kredit' && interest) || (parseAmount(interest) >= 0 && parseAmount(interest) < rest));
  const canSubmit = !busy && (mode === 'items' ? selections.length > 0 && !invalid && reasonOk : accountValid);
  const categories = [...ACCOUNT_CATEGORIES, { key: 'konto', label: 'Anderes Buchungskonto', hint: 'SKR04-Konto direkt angeben.' }];
  const shown = query.trim() ? results.map((item) => ({ item, reasons: [] })) : suggestions;
  const selectedNotShown = selections.filter((s) => !shown.some((c) => c.item.id === s.id)).map((s) => byId.get(s.id)).filter(Boolean);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-xl">
        <SheetHeader className="border-b border-dna-line px-5 py-4 text-left">
          <SheetTitle className="text-[17px] font-semibold text-dna-primary">Transaktion zuordnen</SheetTitle>
          <SheetDescription asChild>
            <div className="space-y-0.5 text-[13px] text-dna-muted">
              {tx && (<>
                <p className="flex items-baseline justify-between gap-3">
                  <span className="truncate font-semibold text-dna-text">{tx.counterparty_name || '—'}</span>
                  <span className={cn('shrink-0 text-[18px] font-semibold tabular-nums', incoming ? 'text-dna-paid-fg' : 'text-dna-due-fg')}>{fmtEUR(tx.amount)}</span>
                </p>
                <p className="break-words">{tx.purpose || 'kein Verwendungszweck'}</p>
                <p>{fmtDate(tx.booking_date)} · {tx.fibu_financial_connections?.display_name}
                  {Number(tx.allocated_amount) > 0 && ` · bereits zugeordnet ${fmtEUR(tx.allocated_amount)}, offen ${fmtEUR(rest)}`}
                  {Number(tx.fees) > 0 && ` · Gebühr ${fmtEUR(tx.fees)} wird auf 6855 gebucht`}</p>
              </>)}
            </div>
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-4 px-5 py-4">
          <Segment value={mode} onChange={setMode} options={[{ key: 'items', label: incoming ? 'Rechnung zuordnen' : 'Beleg zuordnen' }, { key: 'account', label: 'Ohne Beleg buchen' }]} />

          {mode === 'items' && (
            <>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-dna-placeholder" aria-hidden="true" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Rechnung oder Beleg suchen"
                  placeholder={incoming ? 'Rechnungsnr., Kunde oder Betrag' : 'Belegnr., Lieferant oder Betrag'} className={cn(FIELD, 'pl-9')} />
              </div>
              <div className="space-y-2">
                <p className={LABEL}>{query.trim() ? `${results.length} Treffer` : (suggestions.length ? 'Vorschläge' : 'Keine Vorschläge — bitte suchen')}</p>
                {[...selectedNotShown.map((item) => ({ item, reasons: [] })), ...shown].map(({ item, reasons: r }) => (
                  <ItemRow key={item.id} item={item} reasons={r} selected={sel[item.id] !== undefined} onToggle={() => toggle(item)} />
                ))}
              </div>

              {selections.length > 0 && (
                <div className="space-y-3 rounded-xl border border-dna-line p-4">
                  <p className={LABEL}>Zuordnung</p>
                  {selections.map((s) => {
                    const item = byId.get(s.id);
                    if (!item) return null;
                    const bad = !(s.amount > 0) || s.amount > Number(item.open_amount) + 0.005;
                    return (
                      <div key={s.id} className="flex items-center gap-2">
                        <span className="min-w-0 flex-1 truncate text-[14px] text-dna-text"><span className="font-mono">{item.invoice_no}</span> · offen {fmtEUR(item.open_amount)}</span>
                        <Input inputMode="decimal" value={sel[s.id]} aria-label={`Betrag für ${item.invoice_no}`} aria-invalid={bad}
                          onChange={(e) => setSel((p) => ({ ...p, [s.id]: e.target.value }))}
                          className={cn(FIELD, 'w-32 text-right tabular-nums', bad && 'border-dna-due-fg')} />
                        <button type="button" onClick={() => toggle(item)} aria-label={`${item.invoice_no} entfernen`}
                          className="flex h-11 w-11 items-center justify-center rounded-md text-dna-muted hover:bg-dna-warm"><X className="h-4 w-4" /></button>
                      </div>
                    );
                  })}
                  <dl className="space-y-1 border-t border-dna-line pt-3 text-[14px] tabular-nums">
                    <div className="flex justify-between"><dt className="text-dna-muted">Zahlung (offen)</dt><dd className="text-dna-text">{fmtEUR(rest)}</dd></div>
                    <div className="flex justify-between"><dt className="text-dna-muted">Zugeordnet</dt><dd className="text-dna-text">{fmtEUR(diff.sum)}</dd></div>
                    <div className="flex justify-between font-semibold">
                      <dt>Differenz</dt>
                      <dd className={diff.kind === 'exakt' ? 'text-dna-paid-fg' : 'text-dna-due-fg'}>{diff.kind === 'exakt' ? '0,00 € ✓' : fmtEUR(diff.diff)}</dd>
                    </div>
                  </dl>

                  {reasons.length > 0 && (
                    <fieldset className="space-y-2 pt-1">
                      <legend className={cn(LABEL, 'mb-2')}>Differenz begründen</legend>
                      {reasons.map((r) => (
                        <label key={r.key} className={cn('flex cursor-pointer gap-3 rounded-lg border p-3', reason === r.key ? 'border-dna-primary bg-dna-warm' : 'border-dna-line')}>
                          <input type="radio" name="reason" value={r.key} checked={reason === r.key} onChange={() => setReason(r.key)} className="mt-1 h-4 w-4 accent-[#222243]" />
                          <span><span className="block text-[14px] font-semibold text-dna-text">{r.label}</span><span className="block text-[12px] text-dna-muted">{r.hint}</span></span>
                        </label>
                      ))}
                    </fieldset>
                  )}
                </div>
              )}
            </>
          )}

          {mode === 'account' && (
            <fieldset className="space-y-2">
              <legend className={cn(LABEL, 'mb-2')}>Kategorie</legend>
              {categories.map((c) => (
                <label key={c.key} className={cn('flex cursor-pointer gap-3 rounded-lg border p-3', category === c.key ? 'border-dna-primary bg-dna-warm' : 'border-dna-line')}>
                  <input type="radio" name="category" value={c.key} checked={category === c.key} onChange={() => setCategory(c.key)} className="mt-1 h-4 w-4 accent-[#222243]" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] font-semibold text-dna-text">{c.key === 'gesellschafter' && incoming ? 'Einlage Gesellschafter' : c.label}</span>
                    <span className="block text-[12px] text-dna-muted">{c.hint}</span>
                    {category === 'kredit' && c.key === 'kredit' && !incoming && (
                      <span className="mt-2 flex items-center gap-2">
                        <span className="text-[13px] text-dna-muted">davon Zinsen</span>
                        <Input inputMode="decimal" value={interest} onChange={(e) => setInterest(e.target.value)} placeholder="0,00" aria-label="Zinsanteil" className={cn(FIELD, 'w-28 text-right tabular-nums')} />
                      </span>
                    )}
                    {category === 'konto' && c.key === 'konto' && (
                      <Input value={accountNo} onChange={(e) => setAccountNo(e.target.value)} placeholder="z. B. 4980" aria-label="Kontonummer" inputMode="numeric" className={cn(FIELD, 'mt-2 w-32 font-mono')} />
                    )}
                  </span>
                </label>
              ))}
            </fieldset>
          )}

          <label className="block">
            <span className={LABEL}>Notiz (optional)</span>
            <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} className={cn(FIELD, 'mt-1')} />
          </label>
        </div>

        <div className="sticky bottom-0 flex gap-2 border-t border-dna-line bg-white px-5 py-3">
          <Button type="button" variant="outline" className={cn(BTN_SECONDARY, 'flex-1 sm:flex-none')} onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button type="button" className={cn(BTN_PRIMARY, 'flex-1')} disabled={!canSubmit} onClick={submit}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === 'items' ? 'Zuordnen & buchen' : 'Buchen'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
